import type { FeedLog } from '../../types/production'

import { getOfflineDb } from '../dbFactory'

export type FeedLogListParams = {
  farmId?: string
  productionUnitId?: string
  lotId?: string
  lot?: string
  fromRecordedAt?: string
  toRecordedAt?: string
}

export class DexieFeedLogRepo {
  async list(params: FeedLogListParams = {}): Promise<FeedLog[]> {
    const farmId = params.farmId?.trim() || undefined
    const productionUnitId = params.productionUnitId?.trim() || undefined
    const lotId = params.lotId?.trim() || undefined
    const lot = params.lot?.trim() || undefined
    const fromRecordedAt = params.fromRecordedAt?.trim() || undefined
    const toRecordedAt = params.toRecordedAt?.trim() || undefined

    const db = getOfflineDb()
    const table = db.feedLogs

    let base: FeedLog[]

    if (farmId && productionUnitId) {
      base = await table.where('[farmId+productionUnitId]').equals([farmId, productionUnitId]).toArray()
    } else if (farmId) {
      base = await table.where('farmId').equals(farmId).toArray()
    } else if (productionUnitId) {
      base = await table.where('productionUnitId').equals(productionUnitId).toArray()
    } else {
      base = await table.toArray()
    }

    const fromTime = fromRecordedAt ? new Date(fromRecordedAt).getTime() : undefined
    const toTime = toRecordedAt ? new Date(toRecordedAt).getTime() : undefined

    return base
      .filter((log) => {
        if (lotId && (log.lotId ?? '') !== lotId) return false
        if (lot && (log.lot ?? '') !== lot) return false

        if (fromTime !== undefined || toTime !== undefined) {
          const t = new Date(log.recordedAt).getTime()
          if (Number.isNaN(t)) return false
          if (fromTime !== undefined && t < fromTime) return false
          if (toTime !== undefined && t > toTime) return false
        }

        return true
      })
      .sort((a, b) => b.recordedAt.localeCompare(a.recordedAt))
  }

  async getById(id: string): Promise<FeedLog | undefined> {
    return getOfflineDb().feedLogs.get(id)
  }

  async upsert(log: FeedLog): Promise<void> {
    await getOfflineDb().feedLogs.put(log)
  }

  async upsertMany(logs: FeedLog[]): Promise<void> {
    if (logs.length === 0) return
    await getOfflineDb().feedLogs.bulkPut(logs)
  }

  async delete(id: string): Promise<void> {
    await getOfflineDb().feedLogs.delete(id)
  }

  async deleteMany(ids: string[]): Promise<void> {
    if (ids.length === 0) return
    await getOfflineDb().feedLogs.bulkDelete(ids)
  }

  async clear(): Promise<void> {
    await getOfflineDb().feedLogs.clear()
  }
}

export const dexieFeedLogRepo = new DexieFeedLogRepo()
